import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Star, Plus, Check, Trash2, ExternalLink, Wallet, TrendingDown, Tag, RefreshCw } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Spark({ data }) {
  if (!data || data.length < 2) return null;
  const W = 72, H = 18;
  const max = Math.max(...data), min = Math.min(...data), rng = (max - min) || 1;
  const pts = data.map((v, i) => `${(i * (W / (data.length - 1))).toFixed(1)},${(H - ((v - min) / rng) * H).toFixed(1)}`).join(" ");
  const down = data[data.length - 1] <= data[0];
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} data-testid="wish-spark" className="shrink-0">
      <polyline points={pts} fill="none" stroke={down ? "#059669" : "#dc2626"} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function WishList() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [cost, setCost] = useState("");
  const [target, setTarget] = useState("");
  const [link, setLink] = useState("");
  const [savings, setSavings] = useState(0);
  const [savingsInput, setSavingsInput] = useState("");
  const [priceEditId, setPriceEditId] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [checking, setChecking] = useState(null);

  const load = () => api.get("/wishlist").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/profile").then((r) => {
      const s = r.data.monthly_savings || 0;
      setSavings(s);
      setSavingsInput(s ? String(s) : "");
    }).catch(() => {});
  }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Enter an item name");
    await api.post("/wishlist", {
      item_name: name,
      cost: Math.max(0, parseFloat(cost) || 0),
      target_price: Math.max(0, parseFloat(target) || 0),
      link,
    });
    setName(""); setCost(""); setTarget(""); setLink("");
    load();
  };
  const toggle = async (it) => { await api.put(`/wishlist/${it.id}`, { purchased_status: !it.purchased_status }); load(); };
  const remove = async (it) => { await api.delete(`/wishlist/${it.id}`); load(); };
  const saveSavings = async () => {
    const val = Math.max(0, parseFloat(savingsInput) || 0);
    setSavings(val);
    try {
      await api.put("/profile", { monthly_savings: val });
      toast.success("Monthly spare cash updated");
    } catch (e) {}
  };
  const openPriceEditor = (it) => {
    setPriceEditId(it.id);
    setPriceInput(it.current_price != null ? String(it.current_price) : "");
  };
  const savePrice = async (it) => {
    const val = Math.max(0, parseFloat(priceInput) || 0);
    try {
      const { data } = await api.post(`/wishlist/${it.id}/price`, { current_price: val });
      setPriceEditId(null); setPriceInput("");
      if (data.price_drop) toast.success(`${it.item_name} has hit your target price of ${money(data.target_price)}!`);
      else toast.success("Current price updated");
      load();
    } catch (e) { toast.error("Could not update price"); }
  };
  const checkPrice = async (it) => {
    setChecking(it.id);
    try {
      const { data } = await api.post(`/wishlist/${it.id}/check-price`);
      if (data.found) {
        setPriceEditId(it.id);
        setPriceInput(String(data.price));
        toast.success(`Found ${money(data.price)} — review and save`);
      } else {
        setPriceEditId(it.id);
        setPriceInput("");
        toast.info(data.reason === "no_link" ? "Add a product link first, then check" : "Couldn't read the price automatically — enter it manually");
      }
    } catch (e) { toast.error("Price check failed"); } finally { setChecking(null); }
  };

  return (
    <section className="border border-[var(--line)] bg-white h-full flex flex-col" data-testid="wish-list">
      <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
        <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2">
          <Star className="w-5 h-5 text-[var(--accent)]" /> My Wish List
        </h2>
        <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-[var(--text-2)]" data-testid="wish-autocheck-note">
          <RefreshCw className="w-3 h-3" /> Items with a link auto-check daily
        </span>
      </div>

      <div className="p-6 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_110px_110px] gap-2">
          <input data-testid="wish-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Item name" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <input data-testid="wish-cost" type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Cost $" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <input data-testid="wish-target" type="number" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Target $" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <input data-testid="wish-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Link (optional)" className="h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          <button data-testid="wish-add" onClick={add} className="h-11 px-5 bg-[var(--accent)] text-white flex items-center justify-center gap-2 hover:opacity-90 transition-opacity">
            <Plus className="w-4 h-4" /> Add Item
          </button>
        </div>
        <div className="flex items-center gap-2 border border-[var(--line)] bg-[#f9f9f7] p-2">
          <Wallet className="w-4 h-4 text-[var(--accent)] shrink-0" />
          <span className="label-mono text-[var(--text-2)] whitespace-nowrap">Monthly spare cash</span>
          <input data-testid="wish-savings" type="number" step="0.01" value={savingsInput} onChange={(e) => setSavingsInput(e.target.value)} onBlur={saveSavings} placeholder="0.00" className="ml-auto w-28 h-9 px-2 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)] text-right font-mono" />
        </div>
      </div>

      <div className="px-6 pb-6 flex-1 divide-y divide-[var(--line)] border-t border-[var(--line)]">
        {items.length === 0 && <p className="text-sm text-[var(--text-2)] py-6 text-center">Nothing on your wish list yet.</p>}
        {items.map((it) => {
          const effective = it.current_price != null ? it.current_price : it.cost;
          const hist = (it.price_history || []).map((h) => h.price).filter((n) => n > 0);
          const lowest = hist.length ? Math.min(...hist) : null;
          const pct = it.cost > 0 ? Math.min(100, Math.round((savings / it.cost) * 100)) : 100;
          const months = savings > 0 && it.cost > savings ? Math.ceil(it.cost / savings) : it.cost <= savings ? 1 : null;
          return (
            <div key={it.id} data-testid="wish-row" className="py-3 group">
              <div className="flex items-center gap-3">
                <button
                  data-testid="wish-toggle"
                  onClick={() => toggle(it)}
                  className={`w-5 h-5 border-2 flex items-center justify-center shrink-0 transition-colors ${it.purchased_status ? "bg-[var(--accent)] border-[var(--accent)]" : "border-[var(--text-2)] hover:border-[var(--accent)]"}`}
                >
                  {it.purchased_status && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate flex items-center gap-2 ${it.purchased_status ? "line-through text-[var(--text-2)]" : ""}`}>
                    {it.item_name}
                    {it.price_drop && (
                      <span data-testid="wish-price-drop" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold not-italic no-underline shrink-0">
                        <TrendingDown className="w-3 h-3" /> Price Drop
                      </span>
                    )}
                  </p>
                  {it.link && (
                    <a href={it.link} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> view
                    </a>
                  )}
                </div>
                <span className={`font-mono text-sm shrink-0 ${it.price_drop ? "text-emerald-600 font-semibold" : "text-[var(--text-2)]"}`}>{money(effective)}</span>
                <button data-testid="wish-delete" onClick={() => remove(it)} className="w-7 h-7 flex items-center justify-center border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Price breakdown */}
              <div className="mt-1.5 ml-8 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono text-[var(--text-2)]" data-testid="wish-prices">
                <span data-testid="wish-cost-display">Cost {money(it.cost)}</span>
                <span data-testid="wish-current-display">Now {it.current_price != null ? <span className={it.price_drop ? "text-emerald-600 font-semibold" : "text-[var(--text)]"}>{money(it.current_price)}</span> : "—"}</span>
                <span data-testid="wish-target-display">Target {it.target_price > 0 ? money(it.target_price) : "—"}</span>
                {priceEditId !== it.id && (
                  <span className="ml-auto flex items-center gap-3">
                    {it.link && (
                      <button data-testid="wish-check-price" onClick={() => checkPrice(it)} disabled={checking === it.id} className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline disabled:opacity-50">
                        <RefreshCw className={`w-3 h-3 ${checking === it.id ? "animate-spin" : ""}`} /> {checking === it.id ? "Checking…" : "Check Price"}
                      </button>
                    )}
                    <button data-testid="wish-update-price" onClick={() => openPriceEditor(it)} className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline">
                      <Tag className="w-3 h-3" /> Update Price
                    </button>
                  </span>
                )}
              </div>

              {(hist.length >= 2 || lowest != null) && (
                <div className="mt-1.5 ml-8 flex items-center gap-3 text-[11px] font-mono text-[var(--text-2)]" data-testid="wish-history">
                  <Spark data={hist} />
                  {lowest != null && <span data-testid="wish-lowest">Lowest seen {money(lowest)}</span>}
                </div>
              )}

              {priceEditId === it.id && (
                <div className="mt-2 ml-8 flex items-center gap-2" data-testid="wish-price-editor">
                  <input
                    data-testid="wish-price-input" type="number" step="0.01" autoFocus
                    value={priceInput} onChange={(e) => setPriceInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") savePrice(it); if (e.key === "Escape") setPriceEditId(null); }}
                    placeholder="Current price $"
                    className="w-32 h-9 px-2 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)] font-mono text-sm"
                  />
                  <button data-testid="wish-price-save" onClick={() => savePrice(it)} className="h-9 px-3 bg-[var(--accent)] text-white text-xs font-medium hover:opacity-90 transition-opacity">Save</button>
                  <button data-testid="wish-price-cancel" onClick={() => setPriceEditId(null)} className="h-9 px-3 border border-[var(--line)] text-xs hover:border-[var(--accent)] transition-colors">Cancel</button>
                </div>
              )}

              {!it.purchased_status && (
                <div className="mt-2 ml-8" data-testid="wish-progress">
                  <div className="h-1.5 bg-[var(--line)] overflow-hidden">
                    <div className="h-full bg-[var(--accent)] transition-all duration-500" style={{ width: `${savings > 0 ? pct : 0}%` }} />
                  </div>
                  <p className="text-[10px] font-mono text-[var(--text-2)] mt-1">
                    {savings <= 0 ? "Set monthly spare cash to track progress"
                      : pct >= 100 ? "Affordable this month 🎉"
                      : `${pct}% saved · ~${months} mo to go`}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
